import axios from 'axios';

const API_URL =
    (import.meta?.env?.VITE_API_URL || process.env.VUE_APP_API_URL) ||
    'http://localhost:8000/api';

const axiosInstance = axios.create({
    baseURL: API_URL,
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Authentication error:', error.response.data);
        }
        return Promise.reject(error);
    }
);

function toNumberOrNull(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

class LoraApiService {
    /**
     * Fetch all LoRa devices for the caller's tenant from the loramanager gRPC source.
     * @param {Object} [opts]
     * @param {string} [opts.tenantId] - Optional tenant override (watertech admins only)
     * @returns {Promise<{devices: Array}>}
     */
    async listDevices(opts = {}) {
        try {
            const params = new URLSearchParams();
            if (opts.tenantId) params.append('tenant_id', opts.tenantId);
            const url = `/lora/devices${params.toString() ? `?${params}` : ''}`;
            const response = await axiosInstance.get(url);
            const raw = Array.isArray(response.data?.devices) ? response.data.devices : [];
            return { devices: raw.map(this.normalizeLoraDeviceInfo) };
        } catch (error) {
            console.error('Error fetching LoRa devices:', error);
            throw error;
        }
    }

    /**
     * Fetch a single LoRa device snapshot by LDN.
     */
    async getDeviceInfo(ldn) {
        try {
            const response = await axiosInstance.get(`/lora/devices/${encodeURIComponent(ldn)}`);
            return this.normalizeLoraDeviceInfo(response.data);
        } catch (error) {
            console.error('Error fetching LoRa device info:', error);
            throw error;
        }
    }

    /**
     * Fetch history entries for a single LoRa device by LDN.
     */
    async getDeviceHistory(ldn, fromUnix, toUnix) {
        try {
            const params = new URLSearchParams();
            if (fromUnix != null) params.append('from', String(fromUnix));
            if (toUnix != null) params.append('to', String(toUnix));
            const qs = params.toString() ? `?${params}` : '';
            const response = await axiosInstance.get(
                `/lora/devices/${encodeURIComponent(ldn)}/history${qs}`
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching LoRa device history:', error);
            throw error;
        }
    }

    /**
     * Fetch history for all devices in the caller's tenant.
     */
    async listDevicesHistory({ tenantId, fromUnix, toUnix } = {}) {
        try {
            const params = new URLSearchParams();
            if (tenantId) params.append('tenant_id', tenantId);
            if (fromUnix != null) params.append('from', String(fromUnix));
            if (toUnix != null) params.append('to', String(toUnix));
            const qs = params.toString() ? `?${params}` : '';
            const response = await axiosInstance.get(`/lora/devices/history${qs}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching LoRa devices history:', error);
            throw error;
        }
    }

    /**
     * Per-day distinct-device counts for the timeline scrubber.
     * Cheap: server-precomputed via per-tenant per-day hashes.
     */
    async getTenantActivity({ fromUnix, toUnix } = {}) {
        try {
            const params = new URLSearchParams();
            if (fromUnix != null) params.append('from', String(fromUnix));
            if (toUnix != null) params.append('to', String(toUnix));
            const qs = params.toString() ? `?${params}` : '';
            const response = await axiosInstance.get(`/lora/devices/activity${qs}`);
            return Array.isArray(response.data?.days) ? response.data.days : [];
        } catch (error) {
            console.error('Error fetching tenant activity:', error);
            throw error;
        }
    }

    /**
     * The dev_eui list for devices that uplinked on a specific UTC day.
     * Used when the user clicks a bar in the timeline to filter the map.
     */
    async getTenantActivityDevices(day) {
        try {
            const response = await axiosInstance.get(
                `/lora/devices/activity/${encodeURIComponent(day)}`
            );
            return Array.isArray(response.data?.dev_euis) ? response.data.dev_euis : [];
        } catch (error) {
            console.error('Error fetching tenant activity devices:', error);
            throw error;
        }
    }

    /**
     * Normalize a LoraDeviceInfo proto payload. All numeric proto fields are wire-typed as
     * strings; parse them here so consumers can rely on numbers.
     */
    normalizeLoraDeviceInfo(d) {
        if (!d || typeof d !== 'object') return null;
        return {
            dev_eui: d.dev_eui || null,
            dev_addr: d.dev_addr || null,
            device_name: d.device_name || null,
            tenant_id: d.tenant_id || null,
            application_name: d.application_name || null,
            tenant_name: d.tenant_name || null,

            last_seen: d.last_seen || null,
            last_join: d.last_join || null,

            last_rssi: toNumberOrNull(d.last_rssi),
            last_snr: toNumberOrNull(d.last_snr),
            last_dr: toNumberOrNull(d.last_dr),
            last_sf: toNumberOrNull(d.last_sf),
            last_bandwidth: toNumberOrNull(d.last_bandwidth),
            last_frequency: toNumberOrNull(d.last_frequency),
            last_gateway_count: toNumberOrNull(d.last_gateway_count),
            last_gateway_id: d.last_gateway_id || null,
            last_code_rate: d.last_code_rate || null,

            total_uplinks: toNumberOrNull(d.total_uplinks),
            total_joins: toNumberOrNull(d.total_joins),
        };
    }
}

export default new LoraApiService();
